import {
  stagger,
  useAnimate,
  useInView,
  useReducedMotion,
} from 'framer-motion';
import { useTranslation } from 'next-i18next/pages';
import React, { useEffect } from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import TransCustom from '@/components/apps/ui/transCustom/TransCustom';
import { AniSlideUp } from '@/components/shared/animations/basic';
import resolveMotionEase from '@/components/shared/animations/motion/resolveMotionEase';

import theme from '@/styles/theme';

import {
  listDesc,
  listExtra,
  listStep,
  listTitle,
  processLine,
  processList,
  processListContainer,
  processListInner,
  processListWrap,
} from './CustomProcessSection.styles';

export default function CustomProcessSection() {
  const { t } = useTranslation('common');
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(scope, {
    once: true,
    margin: '0px 0px -30% 0px',
  });
  // 솔루션 도입 프로세스 섹션 데이터
  const CUSTOM_PROCESS_DATA = [
    {
      title: t('문의 및 상담'),
      description: t('고객과 솔루션 구축 목적을 상담해요.'),
    },
    {
      title: t('소리 환경 및 데이터셋 검토'),
      description: t(
        '커스텀 엔진 방식을 적용하기 위해 감지하려는 소리와 주변 환경을 검토해요.',
      ),
      extra: t('평균적으로 1개월 소요'),
    },
    {
      title: t('솔루션 개발'),
      description: (
        <TransCustom i18nKey="실제 사운드 AI 엔진을 만들어요. 첫 테스트 가능 버전을 개발합니다." />
      ),
      extra: t('평균적으로 3개월 소요'),
    },
    {
      title: t('실제 환경 테스트'),
      description: t(
        '실제 환경에서 작동 테스트를 해요. 정확도를 측정하고 고객의 의도에 맞는지 체크하고 조율합니다.',
      ),
    },
    {
      title: t('연동 및 배포'),
      description: t('기존 시스템과의 연동 및 배포가 이루어집니다.'),
    },
  ];

  useEffect(() => {
    if (!isDesktop) return;

    const lineInitial = animate('.line', { scaleX: 0 }, { duration: 0 });
    const listInitial = animate(
      '.list-inner',
      { opacity: 0, y: 30 },
      { duration: 0 },
    );
    if (!isInView) {
      return () => {
        lineInitial.stop();
        listInitial.stop();
      };
    }

    const duration = shouldReduceMotion ? 0 : 0.4;
    const controls = animate([
      [
        '.line',
        { scaleX: 1 },
        { duration, ease: resolveMotionEase('power1.out') },
      ],
      [
        '.list-inner',
        { opacity: 1, y: 0 },
        {
          delay: shouldReduceMotion ? 0 : stagger(0.15),
          duration,
          ease: resolveMotionEase('power1.out'),
        },
      ],
    ]);

    return () => {
      lineInitial.stop();
      listInitial.stop();
      controls.stop();
    };
  }, [animate, isDesktop, isInView, shouldReduceMotion]);

  const renderListInner = (
    data: (typeof CUSTOM_PROCESS_DATA)[number],
    index: number,
  ) => {
    const content = (
      <>
        <span className={listStep}>Step {index + 1}</span>
        <h3 className={listTitle}>{data.title}</h3>
        <p className={listDesc}>{data.description}</p>
        {data.extra && <div className={listExtra}>({data.extra})</div>}
      </>
    );

    // .list-inner / .line 은 framer-motion animate() 셀렉터라 그대로 유지한다.
    return isDesktop ? (
      <div className={`${processListInner} list-inner`}>{content}</div>
    ) : (
      <div className={processListInner}>{content}</div>
    );
  };

  return (
    <section>
      <Area>
        <SectionTitle
          color="lighter"
          title={t('솔루션 도입 프로세스')}
          description={
            <TransCustom i18nKey="Listen AI 커스텀 솔루션 구축을 위한 절차를 소개합니다." />
          }
        />

        <div ref={scope} className={processListContainer}>
          {isDesktop && <div className={`${processLine} line`} />}
          <div className={processListWrap}>
            {CUSTOM_PROCESS_DATA.map((data, index) => (
              <div key={index} className={processList}>
                {isDesktop ? (
                  renderListInner(data, index)
                ) : (
                  <AniSlideUp>{renderListInner(data, index)}</AniSlideUp>
                )}
              </div>
            ))}
          </div>
        </div>
      </Area>
    </section>
  );
}
